export function productVideoPlaybackFadeOpacity(currentTime, duration, fadeDuration = 2) {
  const time = Number(currentTime);
  const totalDuration = Number(duration);
  const requestedFadeDuration = Number(fadeDuration);

  if (
    !Number.isFinite(time) ||
    !Number.isFinite(totalDuration) ||
    !Number.isFinite(requestedFadeDuration) ||
    totalDuration <= 0 ||
    requestedFadeDuration <= 0
  ) {
    return 0;
  }

  const clampedTime = Math.min(Math.max(time, 0), totalDuration);
  const effectiveFadeDuration = Math.min(requestedFadeDuration, totalDuration / 2);
  const remainingTime = totalDuration - clampedTime;

  if (remainingTime <= effectiveFadeDuration) {
    return Math.min(1, Math.max(0, 1 - remainingTime / effectiveFadeDuration));
  }

  if (clampedTime <= effectiveFadeDuration) {
    return Math.min(1, Math.max(0, 1 - clampedTime / effectiveFadeDuration));
  }

  return 0;
}

export function shouldAllowNativeProductVideoPinch(isMobile, mediaType) {
  return Boolean(
    isMobile && (mediaType === "video" || mediaType === "external_video")
  );
}

export function shouldLoopDesktopProductVideo(isDesktopViewport) {
  return Boolean(isDesktopViewport);
}

export function productVideoAspectRatio(videoWidth, videoHeight) {
  const width = Number(videoWidth);
  const height = Number(videoHeight);

  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  return width / height;
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  const videoCellSelector =
    ".product-gallery__media:is([data-media-type='video'], [data-media-type='external_video'])";
  const fadeDuration = 2;
  const mobileViewport = window.matchMedia("screen and (max-width: 999px)");
  const desktopViewport = window.matchMedia("screen and (min-width: 1000px)");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const cleanups = new WeakMap();

  function collectVideoCells(scope) {
    const cells = [];

    if (scope.matches?.(videoCellSelector)) {
      cells.push(scope);
    }

    cells.push(...(scope.querySelectorAll?.(videoCellSelector) || []));
    return cells;
  }

  function initializeVideoCell(cell) {
    if (cleanups.has(cell)) return;

    const controller = new AbortController();
    // Prestige reserves iOS pinch gestures for image lightbox zoom. Stop those
    // gestures at video cells so Safari can retain its native viewport zoom.
    const allowNativeMobilePinch = (event) => {
      if (
        shouldAllowNativeProductVideoPinch(
          mobileViewport.matches,
          cell.dataset.mediaType
        )
      ) {
        event.stopPropagation();
      }
    };

    cell.addEventListener("gesturestart", allowNativeMobilePinch, {
      capture: true,
      signal: controller.signal
    });
    cell.addEventListener("gesturechange", allowNativeMobilePinch, {
      capture: true,
      signal: controller.signal
    });

    const video = cell.querySelector("video-media[type='video'] > video");
    const overlay = cell.querySelector(".rr-product-video-loop-fade");

    if (!video || !overlay) {
      cleanups.set(cell, () => {
        controller.abort();
        cleanups.delete(cell);
      });
      return;
    }

    const videoFrame = video.closest("video-media");
    const exactAspectRatioProperty = "--rr-product-video-aspect-ratio";
    let loopModeFrame = null;

    const synchronizeLoopMode = () => {
      loopModeFrame = null;
      video.loop = shouldLoopDesktopProductVideo(desktopViewport.matches);
    };

    const queueLoopModeSynchronization = () => {
      if (loopModeFrame !== null) {
        window.cancelAnimationFrame(loopModeFrame);
      }

      // The mobile gallery owns its one-pass/manual-loop policy below 1000px.
      // Defer the breakpoint handoff until its synchronous layout listener has
      // finished, then make desktop looping authoritative again.
      loopModeFrame = window.requestAnimationFrame(synchronizeLoopMode);
    };

    synchronizeLoopMode();
    desktopViewport.addEventListener("change", queueLoopModeSynchronization);

    const synchronizeAspectRatio = () => {
      const ratio = productVideoAspectRatio(video.videoWidth, video.videoHeight);

      if (ratio !== null) {
        videoFrame?.style.setProperty(exactAspectRatioProperty, String(ratio));
      }
    };

    video.addEventListener("loadedmetadata", synchronizeAspectRatio, {
      signal: controller.signal
    });
    video.addEventListener("resize", synchronizeAspectRatio, {
      signal: controller.signal
    });

    if (video.readyState >= 1) {
      synchronizeAspectRatio();
    }

    let frameId = null;
    let frameType = null;

    const cancelFrame = () => {
      if (frameId === null) return;

      if (frameType === "video" && video.cancelVideoFrameCallback) {
        video.cancelVideoFrameCallback(frameId);
      } else {
        window.cancelAnimationFrame(frameId);
      }

      frameId = null;
      frameType = null;
    };

    const updateOpacity = (mediaTime) => {
      if (reduceMotion.matches) {
        overlay.style.opacity = "0";
        return;
      }

      overlay.style.opacity = productVideoPlaybackFadeOpacity(
        mediaTime,
        video.duration,
        fadeDuration
      ).toFixed(4);
    };

    const scheduleFrame = () => {
      if (
        frameId !== null ||
        video.paused ||
        video.ended ||
        reduceMotion.matches ||
        !cell.isConnected
      ) {
        return;
      }

      if (video.requestVideoFrameCallback) {
        frameType = "video";
        frameId = video.requestVideoFrameCallback(renderFrame);
      } else {
        frameType = "animation";
        frameId = window.requestAnimationFrame(renderFrame);
      }
    };

    const renderFrame = (_now, metadata) => {
      frameId = null;
      frameType = null;
      updateOpacity(metadata?.mediaTime ?? video.currentTime);
      scheduleFrame();
    };

    const synchronize = () => {
      cancelFrame();
      updateOpacity(video.currentTime);
      scheduleFrame();
    };

    const reset = () => {
      cancelFrame();
      overlay.style.opacity = reduceMotion.matches ? "0" : "1";
      videoFrame?.style.removeProperty(exactAspectRatioProperty);
    };

    const handleMotionPreference = () => {
      if (reduceMotion.matches) {
        cancelFrame();
        overlay.style.opacity = "0";
      } else if (!video.paused && !video.ended) {
        synchronize();
      }
    };

    video.addEventListener("play", synchronize, { signal: controller.signal });
    video.addEventListener("seeked", synchronize, { signal: controller.signal });
    video.addEventListener("ratechange", synchronize, { signal: controller.signal });
    video.addEventListener("durationchange", synchronize, { signal: controller.signal });
    video.addEventListener("pause", cancelFrame, { signal: controller.signal });
    video.addEventListener("ended", () => {
      cancelFrame();
      overlay.style.opacity = reduceMotion.matches ? "0" : "1";
    }, { signal: controller.signal });
    video.addEventListener("emptied", reset, { signal: controller.signal });
    reduceMotion.addEventListener("change", handleMotionPreference);

    if (!video.paused && !video.ended) {
      synchronize();
    }

    const cleanup = () => {
      cancelFrame();
      if (loopModeFrame !== null) {
        window.cancelAnimationFrame(loopModeFrame);
      }
      controller.abort();
      desktopViewport.removeEventListener("change", queueLoopModeSynchronization);
      reduceMotion.removeEventListener("change", handleMotionPreference);
      overlay.style.removeProperty("opacity");
      videoFrame?.style.removeProperty(exactAspectRatioProperty);
      cleanups.delete(cell);
    };

    cleanups.set(cell, cleanup);
  }

  function initializeScope(scope = document) {
    collectVideoCells(scope).forEach(initializeVideoCell);
  }

  function cleanupScope(scope) {
    collectVideoCells(scope).forEach((cell) => cleanups.get(cell)?.());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeScope(), { once: true });
  } else {
    initializeScope();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeScope(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    cleanupScope(event.target);
  });
}
